// ============================================================================
// IMPORTS
// ============================================================================
import { seoContent } from '@infinitro/content';
import { get } from 'svelte/store';
import { getLocalePageSEO } from './locale/index.js';
import { casinoOnlySeoConfig, hasSportsCopy, providersSlugMap, SPORTS_ONLY_ROUTES, sportsSlugMap } from './seo-config.js';
// Derive correct company name at bundle time from Vite env - prevents wrong-brand flash on hydration
function initCompanyName() {
    const brand = import.meta.env.VITE_BRAND;
    if (brand === 'kr.fomo.io')
        return '포모 카지노';
    const theme = import.meta.env.VITE_THEME || 'flush';
    switch (theme) {
        case 'fomo':
            return 'Fomo Casino';
        case 'turboslots':
            return 'Turboslot Casino';
        case 'rocketbet':
            return 'Rocketbet Casino';
        case 'luna':
            return 'Lunabet Casino';
        default:
            return 'Flush Crypto Casino';
    }
}
let COMPANY_NAME = initCompanyName();
function initTitleName() {
    const brand = import.meta.env.VITE_BRAND;
    if (brand === 'kr.fomo.io')
        return 'FOMO';
    const theme = import.meta.env.VITE_THEME || 'flush';
    switch (theme) {
        case 'fomo':
            return 'Fomo';
        case 'turboslots':
            return 'Turboslots';
        case 'rocketbet':
            return 'Rocketbet';
        case 'luna':
            return 'Lunabet';
        default:
            return 'Flush';
    }
}
let TITLE_NAME = initTitleName();
const HAS_SPORTS = hasSportsCopy(import.meta.env.VITE_THEME);
// Export sportsMap for backward compatibility
export const sportsMap = sportsSlugMap;
// ============================================================================
// STATE MANAGEMENT
// ============================================================================
// Provider management
let providers = [];
// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================
export function setCompanyName(name) {
    COMPANY_NAME = name;
}
export function setTitleName(name) {
    TITLE_NAME = name;
}
export function getCompanyName() {
    return COMPANY_NAME;
}
export function getTitleName() {
    return TITLE_NAME;
}
export function setProviders(newProviders) {
    providers = newProviders;
}
export function getProviders() {
    return providers;
}
// `/xx/…` overrides bypass `getRoutesConfig`, so they need their own rejection.
function isSportsOnlyPath(pathname) {
    const withoutLang = pathname.replace(/^\/[a-z]{2}(?=\/|$)/, '');
    const clean = withoutLang.split('?')[0].replace(/\/$/, '') || '/';
    return SPORTS_ONLY_ROUTES.includes(clean);
}
function getRoutesConfig() {
    const content = get(seoContent);
    if (HAS_SPORTS)
        return content.routes;
    const routes = {
        ...content.routes,
        ...(content.routesWithoutSports ?? casinoOnlySeoConfig)
    };
    for (const route of SPORTS_ONLY_ROUTES)
        delete routes[route];
    return routes;
}
// Get sports map (always use default - sports slugs are universal)
function getSportsMap() {
    return sportsSlugMap;
}
// ~2 MB of per-game copy and slug data: only game pages need it, so it is fetched on first use.
let gameSeoModule = null;
let gameSeoLoad = null;
const loadGameSeo = () => {
    gameSeoLoad ??= import('./seo-config-games.js').then((module) => (gameSeoModule = module));
    return gameSeoLoad;
};
const getBrandedGameSeo = (pathname, brand) => {
    const pathSeo = gameSeoModule?.getGameSeo(pathname);
    if (!gameSeoModule || !pathSeo)
        return undefined;
    return gameSeoModule.applyGameSeoBrandSubstitution(pathSeo.title, pathSeo.description, brand);
};
/** `/p/{providerSlug}/{gameSlug}` (optional `/{lang}` prefix) - matches game SEO when catalog returns Not Found (e.g. preview). */
function getGameSeoFromSlugMap(pathname) {
    const clean = pathname.replace(/\/$/, '').split('?')[0] || '';
    const pIdx = clean.indexOf('/p/');
    if (pIdx < 0)
        return null;
    const rest = clean.slice(pIdx + '/p/'.length);
    const parts = rest.split('/').filter(Boolean);
    if (parts.length < 2)
        return null;
    const [providerSlug, gameSlug] = parts;
    // Keyed by `{providerSlug}/{gameSlug}` - game slugs repeat across providers.
    const row = gameSeoModule?.gamesSlugMap[`${providerSlug}/${gameSlug}`];
    if (!row || row.providerSlug !== providerSlug)
        return null;
    return { game: row.name, provider: row.provider };
}
// Apply template substitution - matches {var} placeholders used in @content JSON
function applyTemplate(template, vars) {
    let result = template;
    if (vars.game)
        result = result.replace(/\{game\}/g, vars.game);
    if (vars.provider)
        result = result.replace(/\{provider\}/g, vars.provider);
    if (vars.sport)
        result = result.replace(/\{sport\}/g, vars.sport);
    if (vars.brand)
        result = result.replace(/\{brand\}/g, vars.brand);
    return result;
}
// ============================================================================
// CORE SEO FUNCTIONS
// ============================================================================
export function getPageSEO(pathname) {
    const routes = getRoutesConfig();
    // Remove trailing slash and query params
    const cleanPath = pathname.replace(/\/$/, '').split('?')[0] || '/';
    // Try exact match first
    if (routes[cleanPath]) {
        return routes[cleanPath];
    }
    // Try with trailing slash
    if (routes[cleanPath + '/']) {
        return routes[cleanPath + '/'];
    }
    // Handle subdomain paths like /player/vip/ -> /vip (for player site)
    const pathWithoutSubdomain = cleanPath.replace(/^\/[^\/]+/, '');
    if (!pathWithoutSubdomain)
        return null;
    if (pathWithoutSubdomain !== cleanPath && routes[pathWithoutSubdomain]) {
        return routes[pathWithoutSubdomain];
    }
    // Try with trailing slash for subdomain path
    if (pathWithoutSubdomain !== cleanPath && routes[pathWithoutSubdomain + '/']) {
        return routes[pathWithoutSubdomain + '/'];
    }
    return null;
}
// NOTE: Game/provider slugs are extracted from `pageData` (route data), not from the URL path,
// so they aren't affected by path-index issues. Sports slugs are parsed from the pathname using
// `indexOf('sports')` - any future path-based extraction should follow the same pattern rather
// than using hardcoded indices (which break when a locale prefix is present).
export function generateDynamicTitle(pathname, pageData, brandTitle) {
    const brand = brandTitle || getTitleName();
    const patterns = get(seoContent).patterns;
    // Handle game pages with provider
    if (pageData?.game && pageData?.provider_slug) {
        // Skip hardcoded per-game strings on locale paths - fall through to translated patterns
        const hasLocalePrefix = /^\/[a-z]{2}\//.test(pathname);
        const gameSeo = hasLocalePrefix ? undefined : getBrandedGameSeo(pathname, brand);
        if (gameSeo)
            return gameSeo.title;
        const slugMap = getGameSeoFromSlugMap(pathname);
        const provider = providers.find((p) => p.prov === pageData.provider_slug);
        const providerName = provider?.name || '';
        if (pageData.game === 'Not Found') {
            if (slugMap) {
                return applyTemplate(patterns.game_title, {
                    game: slugMap.game,
                    provider: slugMap.provider,
                    brand
                });
            }
            return applyTemplate(patterns.default_title, { brand });
        }
        const resolvedProvider = providerName || slugMap?.provider || '';
        if (!resolvedProvider) {
            return applyTemplate(patterns.default_title, { brand });
        }
        return applyTemplate(patterns.game_title, {
            game: pageData.game,
            provider: resolvedProvider,
            brand
        });
    }
    // Handle provider pages (`/p/{slug}/` - no `game` in page data)
    if (pageData?.provider_slug) {
        const provider = providers.find((p) => p.prov === pageData.provider_slug);
        const providerName = provider?.name || providersSlugMap[pageData.provider_slug] || '';
        if (!providerName) {
            return applyTemplate(patterns.default_title, { brand });
        }
        return applyTemplate(patterns.provider_title, { provider: providerName, brand });
    }
    // Handle sports pages
    if (HAS_SPORTS && pathname.includes('/sports')) {
        const pathParts = pathname.split('/').filter(Boolean);
        const sportsIdx = pathParts.indexOf('sports');
        const sportSlug = sportsIdx >= 0 ? pathParts[sportsIdx + 1] : undefined;
        if (sportSlug && sportSlug !== '') {
            const sportsMapping = getSportsMap();
            const sportName = sportsMapping[sportSlug] ||
                sportSlug
                    .split('-')
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ');
            return applyTemplate(patterns.sport_title, { sport: sportName, brand });
        }
    }
    // Default fallback
    return applyTemplate(patterns.default_title, { brand });
}
export function generateDynamicDescription(pathname, pageData, brandTitle) {
    const brand = brandTitle || getCompanyName();
    const patterns = get(seoContent).patterns;
    // Handle game pages with provider (slot games)
    if (pageData?.game && pageData?.provider_slug) {
        const hasLocalePrefix = /^\/[a-z]{2}\//.test(pathname);
        const gameSeo = hasLocalePrefix ? undefined : getBrandedGameSeo(pathname, brand);
        if (gameSeo)
            return gameSeo.description;
        const slugMap = getGameSeoFromSlugMap(pathname);
        const provider = providers.find((p) => p.prov === pageData.provider_slug);
        const providerName = provider?.name || '';
        if (pageData.game === 'Not Found') {
            if (slugMap && patterns?.game_description) {
                return applyTemplate(patterns.game_description, {
                    game: slugMap.game,
                    provider: slugMap.provider,
                    brand
                });
            }
            return '';
        }
        const resolvedProvider = providerName || slugMap?.provider || '';
        if (!resolvedProvider)
            return '';
        if (patterns?.game_description) {
            return applyTemplate(patterns.game_description, {
                game: pageData.game,
                provider: resolvedProvider,
                brand
            });
        }
        return '';
    }
    // Handle provider pages
    if (pageData?.provider_slug) {
        const provider = providers.find((p) => p.prov === pageData.provider_slug);
        const providerName = provider?.name || providersSlugMap[pageData.provider_slug] || '';
        if (!providerName)
            return '';
        if (patterns?.provider_description) {
            return applyTemplate(patterns.provider_description, { provider: providerName, brand });
        }
        return '';
    }
    // Handle sports pages
    if (HAS_SPORTS && pathname.includes('/sports')) {
        const pathParts = pathname.split('/').filter(Boolean);
        const sportsIdx = pathParts.indexOf('sports');
        const sportSlug = sportsIdx >= 0 ? pathParts[sportsIdx + 1] : undefined;
        if (sportSlug && sportSlug !== '') {
            const sportsMapping = getSportsMap();
            const sportName = sportsMapping[sportSlug] ||
                sportSlug
                    .split('-')
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ');
            if (patterns?.sport_description) {
                return applyTemplate(patterns.sport_description, { sport: sportName, brand });
            }
        }
    }
    return '';
}
// Returns the first element matching the selector and removes any later duplicates. A CSR SPA
// (ssr=false) can end up with a build-shell tag plus a mounted <svelte:head> copy of the same tag;
// keeping one and dropping the rest prevents "multiple meta description"-type audit errors.
function firstAndDropExtras(selector) {
    const nodes = document.querySelectorAll(selector);
    for (let i = 1; i < nodes.length; i++)
        nodes[i].remove();
    return nodes[0] ?? null;
}
export function updateMetaDescription(description) {
    if (typeof document === 'undefined')
        return;
    const content = description.length > 155 ? description.slice(0, 154).trimEnd() + '…' : description;
    let metaDescription = firstAndDropExtras('meta[name="description"]');
    if (metaDescription) {
        metaDescription.setAttribute('content', content);
    }
    else {
        metaDescription = document.createElement('meta');
        metaDescription.setAttribute('name', 'description');
        metaDescription.setAttribute('content', content);
        document.head.appendChild(metaDescription);
    }
}
function upsertMeta(selector, attr, value, content) {
    if (typeof document === 'undefined')
        return;
    let el = firstAndDropExtras(selector);
    if (el) {
        el.setAttribute('content', content);
    }
    else {
        el = document.createElement('meta');
        el.setAttribute(attr, value);
        el.setAttribute('content', content);
        document.head.appendChild(el);
    }
}
export function updateKeywords(keywords) {
    if (typeof document === 'undefined')
        return;
    upsertMeta('meta[name="keywords"]', 'name', 'keywords', keywords);
}
export function updateOgTags(title, description) {
    if (typeof document === 'undefined')
        return;
    upsertMeta('meta[property="og:title"]', 'property', 'og:title', title);
    upsertMeta('meta[property="og:description"]', 'property', 'og:description', description);
    upsertMeta('meta[name="twitter:title"]', 'name', 'twitter:title', title);
    upsertMeta('meta[name="twitter:description"]', 'name', 'twitter:description', description);
}
// Sets the single robots meta tag, never relaxing a noindex the build shell already declared.
// `ssr = false` makes `<svelte:head>` append rather than hydrate, so emitting robots there left two
// tags on every noindex shell - `noindex, follow` plus the runtime's `index,follow,…`. Pages the
// shell deliberately withholds (fallback.html, locale /p/, /recentlyplayed/, /favorites/) then read
// as indexable to crawlers that take the last tag. Keeping the restrictive value matches how Google
// already resolves the conflicting pair, with one unambiguous tag instead of two.
export function updateRobots(content) {
    if (typeof document === 'undefined')
        return;
    const robots = firstAndDropExtras('meta[name="robots"]');
    if (!robots) {
        const el = document.createElement('meta');
        el.setAttribute('name', 'robots');
        el.setAttribute('content', content);
        document.head.appendChild(el);
        return;
    }
    const shellSaysNoindex = /\bnoindex\b/i.test(robots.getAttribute('content') ?? '');
    if (shellSaysNoindex && !/\bnoindex\b/i.test(content))
        return;
    robots.setAttribute('content', content);
}
// Updates canonical URL and related meta tags (og:url, twitter:url)
export function updateCanonicalUrl(url) {
    if (typeof document === 'undefined')
        return;
    // Update canonical link
    let canonical = firstAndDropExtras('link[rel="canonical"]');
    if (canonical) {
        canonical.setAttribute('href', url);
    }
    else {
        canonical = document.createElement('link');
        canonical.setAttribute('rel', 'canonical');
        canonical.setAttribute('href', url);
        document.head.appendChild(canonical);
    }
    // Update og:url
    let ogUrl = firstAndDropExtras('meta[property="og:url"]');
    if (ogUrl) {
        ogUrl.setAttribute('content', url);
    }
    else {
        ogUrl = document.createElement('meta');
        ogUrl.setAttribute('property', 'og:url');
        ogUrl.setAttribute('content', url);
        document.head.appendChild(ogUrl);
    }
    // Update twitter:url
    let twitterUrl = firstAndDropExtras('meta[name="twitter:url"]');
    if (twitterUrl) {
        twitterUrl.setAttribute('content', url);
    }
    else {
        twitterUrl = document.createElement('meta');
        twitterUrl.setAttribute('name', 'twitter:url');
        twitterUrl.setAttribute('content', url);
        document.head.appendChild(twitterUrl);
    }
}
export function setTitleAndDescriptionForSEO(pathname, pageData, brandTitle) {
    if (typeof document === 'undefined')
        return;
    if (pageData?.game && !gameSeoModule) {
        loadGameSeo()
            .then(() => {
            if (window.location.pathname === pathname) {
                setTitleAndDescriptionForSEO(pathname, pageData, brandTitle);
            }
        })
            .catch((error) => console.error('[seo] failed to load game SEO data', error));
    }
    const langMatch = pathname.match(/^\/([a-z]{2})(\/|$)/);
    const lang = langMatch ? langMatch[1] : null;
    const useLocaleOverride = lang !== null && (HAS_SPORTS || !isSportsOnlyPath(pathname));
    const seo = (useLocaleOverride ? getLocalePageSEO(pathname, lang) : null) ?? getPageSEO(pathname);
    if (seo) {
        const brand = brandTitle || getCompanyName();
        const isHome = (lang ? pathname.replace(/^\/[a-z]{2}(?=\/|$)/, '') : pathname).replace(/\/$/, '') === '';
        const titleBrand = brandTitle || (isHome ? brand : getTitleName());
        const title = seo.title.replace(/\{brand\}/g, titleBrand);
        const description = seo.description.replace(/\{brand\}/g, brand);
        document.title = title;
        updateMetaDescription(description);
        updateOgTags(title, description);
        if (seo.keywords)
            updateKeywords(seo.keywords.replace(/\{brand\}/g, brand));
    }
    else {
        // Use dynamic title generation for special pages
        const title = generateDynamicTitle(pathname, pageData, brandTitle);
        document.title = title;
        updateOgTags(title, '');
    }
    // Try to generate dynamic description (will be empty for brands without custom descriptions)
    const dynamicDescription = generateDynamicDescription(pathname, pageData, brandTitle);
    if (dynamicDescription) {
        updateMetaDescription(dynamicDescription);
        updateOgTags(document.title, dynamicDescription);
    }
}
